// The notification inbox (notification-center-and-badge-plan.md §5.3). Defaults to full history,
// newest first, with a toggle to filter down to unread only. Since the underlying query is already
// cursor-paginated (`before`/`limit`), the filter is a real query variable, not a client-side
// filter over an already-fetched page -- filtering client-side after the fact would mean "Load
// more" might need several clicks to surface a handful of unread rows buried in old history. A
// chat pin row (see contexts/notifications.tsx) shows its own chatUnreadCount as a visible chip so
// the bell/badge total is always something a user can find broken out here.
import React, { useCallback, useEffect, useState } from 'react';
import { Done as DoneIcon } from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControlLabel,
  Link as MuiLink,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Stack,
  Switch,
  Typography,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { gql, useClient, useMutation, useQuery } from 'urql';
import { NOTIFICATION_FIELDS, useNotificationsContext } from '../../contexts/notifications';
import {
  FetchAllNotificationsQuery,
  FetchAllNotificationsQueryVariables,
  MarkNotificationPinReadMutation,
  MarkNotificationPinReadMutationVariables,
} from '../../generated/hooks';
import { NotificationLogType } from '../../generated/schemas';

const PAGE_SIZE = 50;

const fetchAllNotifications = gql`
  query fetchAllNotifications($unreadOnly: Boolean!, $before: DateTime) {
    myNotifications(unreadOnly: $unreadOnly, before: $before, limit: 50) {
      ...NotificationFields
    }
  }
  ${NOTIFICATION_FIELDS}
`;

// Deliberately doesn't select `update` -- unlike Chat/index.tsx's own markChatRead call, this fires
// from the inbox page, which isn't subscribed to the league's leagueUpdate channel. The backend still
// broadcasts to whoever IS subscribed (gql/chat.py::_publish_chat_state runs regardless); this tab
// just picks up the new state on its own next refetch instead.
const markNotificationPinRead = gql`
  mutation markNotificationPinRead($input: MarkChatReadInputType!) {
    markChatRead(input: $input) {
      ok
    }
  }
`;

function isPinId(id: string): boolean {
  return id.startsWith('chat:');
}

// The path portion of a row's cta_url, used both as the Link `to` target (so the row is a real
// anchor -- ctrl/cmd/middle-click opens it in a new tab, per the user's request) and by the
// route-change auto-ack effect elsewhere. Absolute (FRONTEND_BASE_URL may differ from this origin
// in dev), so only the pathname is usable for in-app SPA navigation.
function ctaPath(row: NotificationLogType): string | null {
  const ctaUrl = (row.data as { ctaUrl?: string } | null)?.ctaUrl;
  if (!ctaUrl) return null;
  try {
    return new URL(ctaUrl).pathname;
  } catch {
    return null;
  }
}

function formatRelativeTime(iso: string): string {
  const date = new Date(iso);
  const diffMin = Math.round((Date.now() - date.getTime()) / 60000);
  if (diffMin < 1) return 'just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.round(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  const diffDay = Math.round(diffHr / 24);
  if (diffDay < 7) return `${diffDay}d ago`;
  return date.toLocaleDateString();
}

export default function Notifications() {
  const client = useClient();
  const { acknowledge, acknowledgeAll, refetchUnread } = useNotificationsContext();
  const [, markPinReadMutation] = useMutation<
    MarkNotificationPinReadMutation,
    MarkNotificationPinReadMutationVariables
  >(markNotificationPinRead);

  const [showUnreadOnly, setShowUnreadOnly] = useState(false);

  // cache-and-network, not the default cache-first: this page is revisited constantly after taking
  // read actions elsewhere (clicking into chat, acknowledging from the bell dropdown, another tab),
  // and cache-first would keep showing whatever this exact query+variables returned the FIRST time
  // this component ever mounted, forever, until a hard reload. Paints instantly from cache, then
  // silently updates once the fresh network response lands.
  const [{ data, fetching }, refetchFirstPage] = useQuery<
    FetchAllNotificationsQuery,
    FetchAllNotificationsQueryVariables
  >({
    query: fetchAllNotifications,
    variables: { unreadOnly: showUnreadOnly, before: null },
    requestPolicy: 'cache-and-network',
  });
  const [olderRows, setOlderRows] = useState<NotificationLogType[]>([]);
  // null = "not yet determined by an explicit page fetch" -- falls back to the first page's own
  // size below, rather than defaulting to true and showing "Load more" even when everything already
  // fit on page one.
  const [hasMoreOverride, setHasMoreOverride] = useState<boolean | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);

  // Switching the filter invalidates any manually-paged-in older rows -- their cursor position and
  // "does more exist" answer were both computed against the OTHER filter's result set.
  useEffect(() => {
    setOlderRows([]);
    setHasMoreOverride(null);
  }, [showUnreadOnly]);

  const firstPage = (data?.myNotifications ?? []) as NotificationLogType[];
  const rows = [...firstPage, ...olderRows];
  const realRows = rows.filter(r => !isPinId(r.id));
  const firstPageRealCount = firstPage.filter(r => !isPinId(r.id)).length;
  const hasMore = hasMoreOverride ?? firstPageRealCount >= PAGE_SIZE;

  // Any action taken on this page (ack, mark-pin-read, mark-all) can change what page one looks
  // like -- refetch it, and reset the manually-loaded older pages rather than risk them drifting out
  // of sync with a re-fetched, possibly-reordered first page.
  const refreshAfterAction = useCallback(() => {
    setOlderRows([]);
    setHasMoreOverride(null);
    refetchFirstPage({ requestPolicy: 'network-only' });
    refetchUnread();
  }, [refetchFirstPage, refetchUnread]);

  const loadMore = useCallback(async () => {
    // Pins have no history to page through (at most one per league, always "current") -- the cursor
    // comes from the oldest REAL row seen so far, and only real rows go into olderRows.
    const oldest = realRows[realRows.length - 1];
    if (!oldest || loadingMore || !hasMore) return;
    setLoadingMore(true);
    const result = await client
      .query<FetchAllNotificationsQuery, FetchAllNotificationsQueryVariables>(fetchAllNotifications, {
        unreadOnly: showUnreadOnly,
        before: oldest.created,
      })
      .toPromise();
    const page = ((result.data?.myNotifications ?? []) as NotificationLogType[]).filter(r => !isPinId(r.id));
    setHasMoreOverride(page.length === PAGE_SIZE);
    setOlderRows(previous => [...previous, ...page]);
    setLoadingMore(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client, realRows, loadingMore, hasMore, showUnreadOnly]);

  const markPinRead = useCallback(
    async (row: NotificationLogType) => {
      if (!row.league) return;
      await markPinReadMutation({ input: { leagueId: row.league.id, through: new Date().toISOString() } });
      refreshAfterAction();
    },
    [markPinReadMutation, refreshAfterAction],
  );

  const acknowledgeRow = useCallback(
    async (id: string) => {
      await acknowledge([id]);
      refreshAfterAction();
    },
    [acknowledge, refreshAfterAction],
  );

  const handleMarkAllRead = async () => {
    const unreadPins = rows.filter(
      (r): r is NotificationLogType & { league: NonNullable<NotificationLogType['league']> } =>
        isPinId(r.id) && !r.readAt && !!r.league,
    );
    await Promise.all([
      acknowledgeAll(),
      ...unreadPins.map(pin =>
        markPinReadMutation({ input: { leagueId: pin.league.id, through: new Date().toISOString() } }),
      ),
    ]);
    refreshAfterAction();
  };

  // Only the "mark read" side effect -- navigation itself is now handled natively by rendering the
  // row as a real <a> (component={RouterLink}), so ctrl/cmd/middle-click opens it in a new tab.
  const handleRowActivate = (row: NotificationLogType) => {
    if (!isPinId(row.id) && !row.readAt) acknowledgeRow(row.id);
  };

  const anyUnread = rows.some(r => !r.readAt);

  if (fetching && rows.length === 0) {
    return (
      <Box sx={{ mt: 2, textAlign: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" sx={{ py: 2 }}>
        <Typography variant="h4">Notifications</Typography>
        <Stack direction="row" spacing={2} alignItems="center">
          <FormControlLabel
            control={
              <Switch size="small" checked={showUnreadOnly} onChange={(_, checked) => setShowUnreadOnly(checked)} />
            }
            label={<Typography variant="body2">Show unread only</Typography>}
          />
          <Button size="small" onClick={handleMarkAllRead} disabled={!anyUnread}>
            Mark all read
          </Button>
          <MuiLink component={RouterLink} to="/notifications/preferences" underline="hover" variant="body2">
            Notification Preferences
          </MuiLink>
        </Stack>
      </Stack>

      {rows.length === 0 ? (
        <Alert severity="info">
          {showUnreadOnly ? "You're all caught up." : "You don't have any notifications yet."}
        </Alert>
      ) : (
        <List sx={{ maxWidth: 720, mx: 'auto' }}>
          {rows.map(row => {
            const unread = !row.readAt;
            const isPin = isPinId(row.id);
            const targetPath = ctaPath(row);
            return (
              <ListItem
                key={row.id}
                disablePadding
                sx={{ mb: 1, bgcolor: unread ? 'action.hover' : 'transparent', borderRadius: 1 }}
                secondaryAction={
                  unread ? (
                    <Button
                      size="small"
                      startIcon={<DoneIcon fontSize="small" />}
                      onClick={() => (isPin ? markPinRead(row) : acknowledgeRow(row.id))}
                    >
                      Mark read
                    </Button>
                  ) : undefined
                }
              >
                <ListItemButton
                  {...(targetPath ? { component: RouterLink, to: targetPath } : {})}
                  onClick={() => handleRowActivate(row)}
                  sx={{ pr: 14 }}
                >
                  <ListItemText
                    primary={
                      <Stack direction="row" spacing={1} alignItems="center">
                        {unread && (
                          <Box
                            sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'primary.main', flexShrink: 0 }}
                          />
                        )}
                        <Typography variant="body1" sx={{ fontWeight: unread ? 700 : 400 }}>
                          {row.title}
                        </Typography>
                        {typeof row.chatUnreadCount === 'number' && (
                          <Chip size="small" color="primary" label={row.chatUnreadCount} />
                        )}
                      </Stack>
                    }
                    secondary={
                      <>
                        {row.body && (
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            component="span"
                            sx={{ display: 'block' }}
                          >
                            {row.body}
                          </Typography>
                        )}
                        <Typography variant="caption" color="text.secondary">
                          {row.league?.name ? `${row.league.name} · ` : ''}
                          {formatRelativeTime(row.created)}
                        </Typography>
                      </>
                    }
                  />
                </ListItemButton>
              </ListItem>
            );
          })}
        </List>
      )}

      {hasMore && realRows.length > 0 && (
        <Box sx={{ textAlign: 'center', my: 2 }}>
          <Button onClick={loadMore} disabled={loadingMore}>
            {loadingMore ? 'Loading…' : 'Load more'}
          </Button>
        </Box>
      )}
    </>
  );
}
